import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { PushNotifications } from '@capacitor/push-notifications'
import { useEffect } from 'react'

import { useAppState } from '@/capacitor/hooks/useAppState'
import { useMarkMessageAsRead } from '@/capacitor/hooks/useMarkMessageAsRead'
import { usePushNotifications } from '@/capacitor/hooks/usePushNotifications'
import { useNotificationChannel } from '@/modules/healthblocks/hooks/useNotificationChannel'

import type { RegistrationError, Token } from '@capacitor/push-notifications'

const PushNotificationsProvider = () => {
  const { active } = useAppState()
  const { permissionGranted, requestPermissions } = usePushNotifications()
  const { createNotificationChannelIfNotExists } = useNotificationChannel()
  const markMessageAsRead = useMarkMessageAsRead()

  useEffect(() => {
    if (active && permissionGranted) {
      // Trigger "registration" event on PushNotifications
      requestPermissions()
    }
  }, [active, permissionGranted, requestPermissions])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    // Handle push notification registration
    PushNotifications.addListener('registration', (token: Token) => {
      console.debug('Push Notifications registration successful', token)

      // Registration was successful, register with Healthblocks
      Device.getInfo().then((value) => {
        createNotificationChannelIfNotExists(value.name ?? 'Unnamed device', token.value)
      })
    })

    // Handle push notification errors
    PushNotifications.addListener('registrationError', (error: RegistrationError) => {
      console.debug('Push Notifications registration error', error)
    })

    // Handle received notifications
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.debug('Push notification received:', notification)
    })

    // Handle notification actions
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.debug('Push notification action performed:', notification)

      // Mark the message as read when user interacts with the notification
      const messageId = notification.notification?.data?.messageId
      if (messageId) {
        markMessageAsRead(messageId)
      }
    })

    return () => {
      PushNotifications.removeAllListeners()
    }
  }, [createNotificationChannelIfNotExists, markMessageAsRead])

  return null
}

export { PushNotificationsProvider }
